import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Check, X, Tag, Gift, 
  DollarSign, Percent, Calendar, Users, Package, Clock,
  Copy, Archive, Eye, EyeOff, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function
const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

// ==================== PLAN CARD COMPONENT ====================
const PlanCard = ({ plan, onEdit, onToggleStatus }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className={`relative ${plan.status !== 'activo' ? 'opacity-60' : ''}`}>
      <div 
        className="absolute top-0 left-0 w-1 h-full rounded-l-lg"
        style={{ backgroundColor: plan.color }}
      />
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{plan.nombre}</CardTitle>
              {plan.es_recomendado && (
                <Badge className="bg-amber-500">Recomendado</Badge>
              )}
              {plan.etiqueta_destacada && (
                <Badge variant="outline">{plan.etiqueta_destacada}</Badge>
              )}
            </div>
            <CardDescription className="mt-1">
              {plan.descripcion_breve}
            </CardDescription>
          </div>
          <Badge variant={plan.status === 'activo' ? 'default' : 'secondary'}>
            {plan.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500">Precio Mensual</p>
              <p className="text-xl font-bold" style={{ color: plan.color }}>
                {plan.pricing?.precio_mensual > 0 
                  ? formatCLP(plan.pricing.precio_mensual)
                  : plan.requiere_cotizacion ? 'Cotizar' : 'Gratis'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Implementación</p>
              <p className="text-lg font-semibold">
                {plan.pricing?.valor_implementacion > 0 
                  ? formatCLP(plan.pricing.valor_implementacion)
                  : plan.pricing?.implementacion_incluida ? 'Incluida' : '-'}
              </p>
            </div>
          </div>
          
          {/* Discounts */}
          <div className="flex gap-2">
            <Badge variant="outline" className="text-green-600">
              <Percent className="h-3 w-3 mr-1" />
              {plan.pricing?.descuento_anual_porcentaje || 20}% anual
            </Badge>
            <Badge variant="outline" className="text-blue-600">
              <Percent className="h-3 w-3 mr-1" />
              {plan.pricing?.descuento_compromiso_anual || 10}% compromiso
            </Badge>
          </div>
          
          {/* Features Preview */}
          <div>
            <button 
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {plan.features?.length || 0} características
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1">
                {plan.features?.map((f, i) => (
                  <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                    <Check className="h-3 w-3 text-green-500" />
                    {f}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => onEdit(plan)}>
              <Edit2 className="h-4 w-4 mr-1" />
              Editar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onToggleStatus(plan)}>
              {plan.status === 'activo' ? (
                <><EyeOff className="h-4 w-4 mr-1" /> Desactivar</>
              ) : (
                <><Eye className="h-4 w-4 mr-1" /> Activar</>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== ADDON CARD COMPONENT ====================
const AddonCard = ({ addon, onEdit }) => (
  <Card className={addon.is_active ? '' : 'opacity-60'}>
    <CardContent className="pt-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <h4 className="font-semibold">{addon.nombre}</h4>
            <Badge variant="outline" className="text-xs">
              {addon.categoria}
            </Badge>
          </div>
          <p className="text-sm text-slate-600 mt-1">{addon.descripcion}</p>
          <div className="flex gap-4 mt-3">
            {addon.precio_mensual > 0 && (
              <div>
                <span className="text-xs text-slate-500">Mensual</span>
                <p className="font-semibold">{formatCLP(addon.precio_mensual)}</p>
              </div>
            )}
            {addon.precio_anual > 0 && (
              <div>
                <span className="text-xs text-slate-500">Anual</span>
                <p className="font-semibold">{formatCLP(addon.precio_anual)}</p>
              </div>
            )}
            {addon.precio_unico > 0 && (
              <div>
                <span className="text-xs text-slate-500">Único</span>
                <p className="font-semibold">{formatCLP(addon.precio_unico)}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(addon)}>
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// ==================== PROMO CARD COMPONENT ====================
const PromoCard = ({ promo, onEdit }) => (
  <Card className={promo.is_active ? '' : 'opacity-60'}>
    <CardContent className="pt-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-purple-600" />
            <code className="px-2 py-1 bg-purple-100 rounded font-mono text-sm">
              {promo.codigo}
            </code>
            <Badge variant={promo.is_active ? 'default' : 'secondary'}>
              {promo.is_active ? 'Activo' : 'Inactivo'}
            </Badge>
          </div>
          <p className="text-sm font-medium mt-2">{promo.nombre}</p>
          <p className="text-sm text-slate-600">{promo.descripcion}</p>
          <div className="flex gap-4 mt-3">
            <div className="flex items-center gap-1 text-sm">
              {promo.tipo === 'porcentaje' ? (
                <><Percent className="h-4 w-4" /> {promo.valor}% descuento</>
              ) : promo.tipo === 'monto_fijo' ? (
                <><DollarSign className="h-4 w-4" /> {formatCLP(promo.valor)} descuento</>
              ) : (
                <><Gift className="h-4 w-4" /> {promo.valor} meses gratis</>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-slate-600">
              <Users className="h-4 w-4" />
              {promo.usos_actuales || 0}/{promo.max_usos_total || '∞'} usos
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(promo)}>
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// ==================== QUOTE ROW COMPONENT ====================
const QuoteRow = ({ quote, onView }) => (
  <tr className="border-b hover:bg-slate-50">
    <td className="py-3 px-4">
      <div>
        <p className="font-medium">{quote.nombre_institucion}</p>
        <p className="text-sm text-slate-600">{quote.nombre_contacto}</p>
      </div>
    </td>
    <td className="py-3 px-4">
      <p className="text-sm">{quote.email_contacto}</p>
      <p className="text-sm text-slate-600">{quote.telefono_contacto || '-'}</p>
    </td>
    <td className="py-3 px-4">
      <Badge variant="outline">{quote.plan_interes || 'No especificado'}</Badge>
    </td>
    <td className="py-3 px-4">
      <Badge 
        variant={
          quote.status === 'pendiente' ? 'default' :
          quote.status === 'aceptada' ? 'success' :
          quote.status === 'rechazada' ? 'destructive' : 'secondary'
        }
      >
        {quote.status}
      </Badge>
    </td>
    <td className="py-3 px-4">
      {quote.quote_amount ? formatCLP(quote.quote_amount) : '-'}
    </td>
    <td className="py-3 px-4">
      <Button variant="ghost" size="sm" onClick={() => onView(quote.id)}>
        Ver
      </Button>
    </td>
  </tr>
);

// ==================== PROMO MODAL COMPONENT ====================
const PromoModal = ({ open, onClose, selectedItem, token, onSave }) => {
  const [formData, setFormData] = useState(selectedItem || {
    codigo: '',
    nombre: '',
    descripcion: '',
    tipo: 'porcentaje',
    valor: 0,
    alcance: 'todos_planes',
    max_usos_total: null,
    is_active: true
  });
  
  useEffect(() => {
    setFormData(selectedItem || {
      codigo: '',
      nombre: '',
      descripcion: '',
      tipo: 'porcentaje',
      valor: 0,
      alcance: 'todos_planes',
      max_usos_total: null,
      is_active: true
    });
  }, [selectedItem]);
  
  const handleSave = async () => {
    try {
      const method = selectedItem?.id ? 'PUT' : 'POST';
      const url = selectedItem?.id 
        ? `${API_URL}/api/promos/${selectedItem.id}`
        : `${API_URL}/api/promos`;
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success(selectedItem?.id ? 'Código actualizado' : 'Código creado');
        onSave();
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedItem?.id ? 'Editar' : 'Nuevo'} Código Promocional
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Código</Label>
            <Input 
              value={formData.codigo}
              onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})}
              placeholder="SIMUCLINIC20"
              disabled={!!selectedItem?.id}
            />
          </div>
          <div>
            <Label>Nombre</Label>
            <Input 
              value={formData.nombre}
              onChange={(e) => setFormData({...formData, nombre: e.target.value})}
              placeholder="Descuento de bienvenida"
            />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea 
              value={formData.descripcion}
              onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo</Label>
              <Select 
                value={formData.tipo}
                onValueChange={(v) => setFormData({...formData, tipo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="porcentaje">Porcentaje</SelectItem>
                  <SelectItem value="monto_fijo">Monto Fijo</SelectItem>
                  <SelectItem value="meses_gratis">Meses Gratis</SelectItem>
                  <SelectItem value="implementacion_gratis">Implementación Gratis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor</Label>
              <Input 
                type="number"
                value={formData.valor}
                onChange={(e) => setFormData({...formData, valor: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <div>
            <Label>Máximo de usos (vacío = ilimitado)</Label>
            <Input 
              type="number"
              value={formData.max_usos_total || ''}
              onChange={(e) => setFormData({...formData, max_usos_total: e.target.value ? parseInt(e.target.value) : null})}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch 
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({...formData, is_active: v})}
            />
            <Label>Activo</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== MAIN COMPONENT ====================
export default function CommercialManagementPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [promos, setPromos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  
  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [plansRes, addonsRes, promosRes, quotesRes] = await Promise.all([
        fetch(`${API_URL}/api/plans?include_archived=true`, { headers }),
        fetch(`${API_URL}/api/addons?activos_only=false`, { headers }),
        fetch(`${API_URL}/api/promos?activos_only=false`, { headers }),
        fetch(`${API_URL}/api/quotes`, { headers })
      ]);
      
      if (plansRes.ok) setPlans(await plansRes.json());
      if (addonsRes.ok) {
        const data = await addonsRes.json();
        setAddons(data.addons || []);
      }
      if (promosRes.ok) {
        const data = await promosRes.json();
        setPromos(data.promos || []);
      }
      if (quotesRes.ok) {
        const data = await quotesRes.json();
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };
  
  const handleEditPlan = (plan) => {
    setSelectedItem(plan);
    setShowPlanModal(true);
  };
  
  const handleTogglePlanStatus = async (plan) => {
    try {
      const endpoint = plan.status === 'activo' ? 'deactivate' : 'activate';
      const res = await fetch(`${API_URL}/api/plans/${plan.id}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Plan ${endpoint === 'activate' ? 'activado' : 'desactivado'}`);
        loadData();
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };
  
  const handleEditAddon = (addon) => {
    setSelectedItem(addon);
    setShowAddonModal(true);
  };
  
  const handleEditPromo = (promo) => {
    setSelectedItem(promo);
    setShowPromoModal(true);
  };
  
  const handleViewQuote = (quoteId) => {
    navigate(`/admin/quotes/${quoteId}`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="commercial-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Gestión Comercial</h1>
            <p className="text-slate-600">Planes, complementos, promociones y cotizaciones</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Planes ({plans.length})
          </TabsTrigger>
          <TabsTrigger value="addons" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Complementos ({addons.length})
          </TabsTrigger>
          <TabsTrigger value="promos" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Promociones ({promos.length})
          </TabsTrigger>
          <TabsTrigger value="quotes" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Cotizaciones ({quotes.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Gestiona los planes disponibles para tus clientes
            </p>
            <Button onClick={() => navigate('/app/admin/plans')}>
              <Edit2 className="h-4 w-4 mr-2" />
              Editor Avanzado
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {plans.map(plan => (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                onEdit={handleEditPlan}
                onToggleStatus={handleTogglePlanStatus}
              />
            ))}
          </div>
        </TabsContent>
        
        {/* Addons Tab */}
        <TabsContent value="addons" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Complementos que pueden añadirse a cualquier plan
            </p>
            <Button onClick={() => {
              setSelectedItem(null);
              setShowAddonModal(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Complemento
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addons.map(addon => (
              <AddonCard key={addon.id} addon={addon} onEdit={handleEditAddon} />
            ))}
          </div>
        </TabsContent>
        
        {/* Promos Tab */}
        <TabsContent value="promos" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Códigos de descuento y promociones
            </p>
            <Button onClick={() => {
              setSelectedItem(null);
              setShowPromoModal(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Código
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promos.map(promo => (
              <PromoCard key={promo.id} promo={promo} onEdit={handleEditPromo} />
            ))}
            {promos.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center">
                  <Tag className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-600">No hay códigos promocionales</p>
                  <Button 
                    className="mt-4"
                    onClick={() => {
                      setSelectedItem(null);
                      setShowPromoModal(true);
                    }}
                  >
                    Crear primer código
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        {/* Quotes Tab */}
        <TabsContent value="quotes" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              Solicitudes de cotización recibidas
            </p>
          </div>
          {quotes.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium">Institución</th>
                      <th className="py-3 px-4 text-left text-sm font-medium">Contacto</th>
                      <th className="py-3 px-4 text-left text-sm font-medium">Plan</th>
                      <th className="py-3 px-4 text-left text-sm font-medium">Estado</th>
                      <th className="py-3 px-4 text-left text-sm font-medium">Monto</th>
                      <th className="py-3 px-4 text-left text-sm font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map(quote => (
                      <QuoteRow key={quote.id} quote={quote} onView={handleViewQuote} />
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No hay cotizaciones pendientes</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Promo Modal */}
      <PromoModal 
        open={showPromoModal}
        onClose={() => {
          setShowPromoModal(false);
          setSelectedItem(null);
        }}
        selectedItem={selectedItem}
        token={token}
        onSave={loadData}
      />
    </div>
  );
}
